/*
 * zod building blocks shared by every form schema. Values are kept as the
 * plain strings the inputs bind to (no `.trim()` transforms), so a schema's
 * input and output types stay identical and react-hook-form's generics line up.
 */
import { z } from "zod";

const hasText = (v: string) => v.trim().length > 0;

/** Non-blank string. */
export const requiredText = (label: string) =>
  z.string().refine(hasText, `${label} is required.`);

/** Any string, blanks allowed. */
export const optionalText = () => z.string();

/** `YYYY-MM-DD` or blank. */
export const dateString = () =>
  z
    .string()
    .refine(
      (v) => !hasText(v) || /^\d{4}-\d{2}-\d{2}$/.test(v.trim()),
      "Enter a valid date."
    );

/** Non-blank `YYYY-MM-DD`. */
export const requiredDate = (label: string) =>
  z
    .string()
    .refine(hasText, `${label} is required.`)
    .refine((v) => /^\d{4}-\d{2}-\d{2}$/.test(v.trim()), "Enter a valid date.");

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/** Blank or a plausible email. */
export const optionalEmail = () =>
  z.string().refine((v) => !hasText(v) || EMAIL_RE.test(v.trim()), {
    message: "Enter a valid email address."
  });

/**
 * Blank or something with a few digits in it. Deliberately loose: legacy
 * records hold short/partial numbers and must stay saveable.
 */
export const optionalPhone = () =>
  z.string().refine((v) => !hasText(v) || v.replace(/\D/g, "").length >= 3, {
    message: "Enter a valid phone number."
  });

export type AddressShape = {
  street: string;
  city: string;
  state: string;
  zipCode: string;
  country: string;
};

/**
 * Street / city / state / ZIP / country. Required means every part must be
 * filled; otherwise all parts may be blank.
 */
export const addressSchema = (opts: { required?: boolean } = {}) =>
  opts.required
    ? z.object({
        street: requiredText("Street address"),
        city: requiredText("City"),
        state: requiredText("State / province"),
        zipCode: requiredText("ZIP / postal code"),
        country: requiredText("Country")
      })
    : z.object({
        street: optionalText(),
        city: optionalText(),
        state: optionalText(),
        zipCode: optionalText(),
        country: optionalText()
      });

/**
 * Adds "end must not be before start" to an object schema with two
 * `YYYY-MM-DD` fields. Only checks when both are present.
 */
export const endDateAfterStart = <T extends Record<string, unknown>>(
  schema: z.ZodType<T, T>,
  startKey: keyof T & string,
  endKey: keyof T & string,
  message = "End date can't be before the start date."
): z.ZodType<T, T> =>
  schema.superRefine((v, ctx) => {
    const start = String(v[startKey] ?? "").trim();
    const end = String(v[endKey] ?? "").trim();
    if (start && end && end < start) {
      ctx.addIssue({ code: "custom", message, path: [endKey] });
    }
  });

/** Attaches an issue to `path` when `when` holds and the field is blank. */
export const requiredWhen = <T extends Record<string, unknown>>(
  schema: z.ZodType<T, T>,
  key: keyof T & string,
  when: (v: T) => boolean,
  message: string
): z.ZodType<T, T> =>
  schema.superRefine((v, ctx) => {
    if (when(v) && !hasText(String(v[key] ?? ""))) {
      ctx.addIssue({ code: "custom", message, path: [key] });
    }
  });
